<template>
  <article
    class="base-block-epi"
    :class="[sizeClass, highlightClass, layoutClass, pointerClass]"
  >
    <div
      class="base-block-epi__inner-wrapper"
      :class="{
        'row-noGutter': !isLayout.mediaTop,
        'row-column-noGutter': isLayout.mediaTop
      }"
      @click="simulateClickOnLink"
    >
      <div
        :class="{
          'row-noGutter col-12': isLayout.mediaRightSmall,
          'col-last row-noGutter': isLayout.mediaTop,
          'col-6': isLayout.mediaLeft,
          'col-8': isLayout.mediaRightSecondary,
          'row-column-noGutter':
            isLayout.mediaLeft || isLayout.mediaRightSecondary
        }"
      >
        <div
          class="row-noGutter"
          :class="{
            'col': isLayout.mediaLeft || isLayout.mediaRightSecondary,
            'col-12': isLayout.mediaTop || isLayout.mediaRightSmall
          }"
        >
          <div
            v-if="isLayout.mediaRightSmall"
            class="base-block-epi__media col-5"
          >
            <slot name="media" />
          </div>
          <div
            class="base-block-epi__content row-noGutter"
            :class="{
              'col-7': isLayout.mediaRightSmall,
              'col-12': isLayout.mediaRightSecondary || isLayout.mediaLeft
            }"
          >
            <div class="base-block-epi__content__padding">
              <slot name="content" />
            </div>
          </div>
        </div>
        <slot name="footer" />
      </div>
      <div
        v-if="!isLayout.mediaRightSmall"
        class="base-block-epi__media-lg"
        :class="{
          'col': isLayout.mediaTop,
          'col-4': isLayout.mediaRightSecondary,
          'col-6-first': isLayout.mediaLeft
        }"
      >
        <slot name="media" />
      </div>
    </div>
  </article>
</template>

<script>
export const layoutTypes = {
  mediaTop: "mediaTop",
  mediaLeft: "mediaLeft",
  mediaRightSecondary: "mediaRightSecondary",
  mediaRightSmall: "mediaRightSmall"
}

export default {
  name: "BaseBlockEpiComponent",
  props: {
    ariaLabelText: {
      type: String,
      default: "Länkblock"
    },
    url: {
      type: String,
      default: ""
    },
    size: {
      type: String,
      default: "quarter",
      validator (value) {
        return ["full", "half", "third", "quarter"].includes(value)
      }
    },
    centerContent: {
      type: Boolean,
      default: true
    },
    layoutType: {
      type: String,
      default: layoutTypes.mediaTop,
      validator (value) {
        return Object.keys(layoutTypes).includes(value)
      }
    }
  },
  computed: {
    isSize () {
      return {
        [this.size]: true
      }
    },
    isLayout () {
      return {
        [this.layoutType]: true
      }
    },
    sizeClass () {
      return `base-block-epi--${this.size}`
    },
    pointerClass () {
      return this.url ? '' : 'no-pointer-cursor'
    },
    isHighlight () {
      return ["full", "half"].includes(this.size)
    },
    highlightClass () {
      if (!this.$root.isLargeViewport && this.isHighlight) {
        return "base-block-epi--highlight"
      }

      return ""
    },
    layoutClass () {
      return `base-block-epi--${this.$options.filters.kebab(this.layoutType)}`
    }
  },
  methods: {
    simulateClickOnLink () {
      if (this.url) {
        this.$emit('simulateClick')
      }
    }
  }
}
</script>

<style lang="scss">
$base-height-up-lg: 32.5rem;

[class*="row-"]:not([class*="col-"]) {
  flex-basis: auto;
}

.base-block-epi {
  $base: &;

  box-shadow: $box-shadow-primary;

  h3 {
    margin: 0;
  }

  > a {
    color: inherit;

    &:hover,
    &:active {
      color: inherit;
    }
  }

  &:hover {
    box-shadow: 0.06rem 0.1225rem 0.225rem 0 rgba(0, 0, 0, 0.06);
  }

  &.no-pointer-cursor {
    cursor: auto;
  }

  &__content {
    width: 100%;
    text-align: center;

    &__padding {
      padding: 1rem;
      width: 100%;

      @include media-breakpoint-up(md) {
        margin: auto;
        padding: 2.5rem 5rem;
      }

      @include media-breakpoint-up(lg) {
        padding: 1rem;
      }

      #{$base}--highlight & {
        padding: 1.5rem 1.5rem;

        @include media-breakpoint-up(md) {
          margin: auto;
          padding: 3rem;
        }
      }

      .base-block-epi.base-block-epi--third .base-block-epi__content & {
        @include media-breakpoint-up(lg) {
          padding: 1.5rem 1rem;
        }
      }

      .base-block-epi.base-block-epi--half .base-block-epi__content & {
        @include media-breakpoint-up(lg) {
          padding: 2rem 3rem 2.5rem;
        }
      }

      .base-block-epi.base-block-epi--full .base-block-epi__content & {
        @include media-breakpoint-up(lg) {
          padding: 4.5rem 4rem;
        }

        @include media-breakpoint-up(xl) {
          padding: 5.5rem 5rem;
        }
      }
    }
  }

  &__inner-wrapper {
    border-radius: 4px;
    background: $color-white;
    // height: 100%;
    flex-wrap: nowrap;

    @include media-breakpoint-up(lg) {
      height: $base-height-up-lg;

      .base-block-epi__content,
      .base-block-epi__content__padding {
        max-height: $base-height-up-lg;
        overflow: hidden;
      }
    }
  }

  // Default font sizes - third & quarter
  .base-block-epi__content {
    [class$="__prefix"] {
      @include font(hand, 1, "normal");

      color: $color-red;
    }

    [class$="__title"] {
      overflow-wrap: break-word;
      word-wrap: break-word;
      hyphens: auto;

      @include font(rubrik, 2, "bold");
    }

    [class$="__preamble"] {
      @include font(text, 3, "light");
    }
  }

  // Font sizes full & half
  &--full,
  &--half {
    .base-block-epi__content {
      [class$="__prefix"] {
        @include font(hand, 2, "normal");

        color: $color-red;
      }

      [class$="__title"] {
        @include font(rubrik, 3, "bold");
      }

      [class$="__preamble"] {
        margin-top: 1.5rem;
        max-height: 100%;
        overflow: hidden;
      }
    }
  }

  @include media-breakpoint-up(lg) {
    &--full {
      .base-block-epi__content [class$="__preamble"] {
        @include font(text, 4, "light");
      }
    }
  }

  @include media-breakpoint-up(lg) {
    &--full {
      .base-block-epi {
        &__inner-wrapper {
          min-height: 36.5rem;
          height: auto;
        }

        &__content,
        &__content__padding {
          max-height: none;
          margin: auto;
        }
      }
    }
  }

  @include media-breakpoint-down(md) {
    &.base-block-epi--highlight .base-block-epi__inner-wrapper {
      min-height: 25rem;

      .base-block-epi__media-lg > div:not(.coupon-tag) {
        min-height: 25rem;
      }
    }
  }

  @include media-breakpoint-down(sm) {
    &.base-block-epi--highlight .base-block-epi__inner-wrapper {
      min-height: 30rem;

      .base-block-epi__media-lg > div:not(.coupon-tag) {
        min-height: 100%;
      }
    }
  }

  &__media .epi-picture {
    height: 100%;

    div {
      border-radius: 4px;
      height: 100%;
    }
  }

  & &__media-lg {
    flex-basis: auto;
    flex-grow: 1;
    display: flex;

    > div {
      width: 100%;
    }

    .epi-picture {
      flex-grow: 1;
      display: flex;

      > div {
        flex-grow: 1;
        display: flex;
      }

      @at-root {
        @include media-breakpoint-down(sm) {
          #{$base} .base-block-epi__inner-wrapper &,
          #{$base} .base-block-epi__inner-wrapper {
            border-radius: 0 0 0 0;
          }
        }
      }
    }
  }

  @include media-breakpoint-up(md) {
    &#{$base}--media-top #{$base}__media-lg .epi-picture {
      border-radius: 4px 4px 0 0;
    }

    &#{$base}--media-left #{$base}__media-lg .epi-picture {
      border-radius: 4px 0 0 4px;
    }

    &#{$base}--media-right-secondary #{$base}__media-lg .epi-picture {
      border-radius: 0 4px 4px 0;
    }
  }
}
</style>
